import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { colors, fonts, animate } from '../../styles/theme';
import Checkbox from '../checkbox/Checkbox';
import { Button } from '../buttons';
import { Icon } from '../../ui_elements/icons';

const inputStyles = css`
  font-family: ${fonts.sans.family};
  font-size: ${fonts.sans.size.normal};
  appearance: none;
  border-radius: 3px;
  border: 1px solid ${colors.grey.secondaryDark};
  box-shadow: none;
  margin: 0;
  outline: none;
  padding: 7px 8px;
  transition: all ${animate.transition} ease;
  width: 100%;
  max-width: 100%;
  display: block;
  box-sizing: border-box;
  min-height: 38px;
  line-height: inherit;

  &::placeholder {
    color: ${colors.grey.altDark};
  }

  &:focus {
    border: 1px solid ${colors.blue.secondaryDark};
  }

  &:disabled {
    color: ${colors.grey.primaryLight};
    background: ${colors.grey.altDark};
    border-color: ${colors.grey.altDark};
  }
`;

const inputPropTypes = {
  placeholder: PropTypes.string.isRequired,
  id: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium']),
};

const inputDefaultProps = {
  placeholder: '',
  type: 'text',
  required: false,
  size: 'medium',
};

export const Input = styled('input')`
  ${inputStyles};
  height: ${(props) => (props.size === 'small' ? '30px' : '40px')};
`;
Input.propTypes = {
  ...inputPropTypes,
};
Input.defaultProps = {
  ...inputDefaultProps,
};

const InlineCheckboxWrapper = styled('div')`
  position: relative;
`;

const InlineWrapper = styled('div')`
  overflow: hidden;
`;

const CheckboxToggle = styled('div')`
  box-sizing: border-box;
  background-color: ${colors.grey.secondary};
  border: 1px solid ${colors.grey.secondaryDark};
  border-left: 0;
  float: right;
  padding: 10px 10px 0;
  height: 40px;
  + ${InlineWrapper} ${Input} {
    border-radius: 3px 0 0 3px;
  }
`;

export const InputWithCheckbox = ({
  checkboxLabel,
  onInputChange,
  onCheckboxChange,
  inputValue,
  checkboxValue,
  placeholder,
  inputProps,
}) => {
  return (
    <InlineCheckboxWrapper>
      <CheckboxToggle>
        <Checkbox label={checkboxLabel} onChange={onCheckboxChange} checked={checkboxValue} />
      </CheckboxToggle>
      <InlineWrapper>
        <Input
          {...inputProps}
          placeholder={placeholder}
          value={inputValue}
          onChange={onInputChange}
        />
      </InlineWrapper>
    </InlineCheckboxWrapper>
  );
};

InputWithCheckbox.propTypes = {
  checkboxLabel: PropTypes.string.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  inputValue: PropTypes.string,
  checkboxValue: PropTypes.bool.isRequired,
  placeholder: PropTypes.string,
  inputProps: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
};

InputWithCheckbox.defaultProps = {
  inputValue: '',
  placeholder: '',
  inputProps: {
    name: '',
    value: '',
  },
};

export const InputTypeDate = styled(Input)`
  &::-webkit-inner-spin-button,
  &::-webkit-calendar-picker-indicator {
    display: none;
    appearance: none;
  }
`;
InputTypeDate.propTypes = {
  ...inputPropTypes,
};

InputTypeDate.defaultProps = {
  ...inputDefaultProps,
  type: 'date',
};

export const InputTypeNumber = styled(Input)`
  appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
  }
`;
InputTypeNumber.propTypes = {
  ...inputPropTypes,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  value: PropTypes.number,
};

InputTypeNumber.defaultProps = {
  ...inputDefaultProps,
  type: 'number',
};

// to do: this should be like radio and radioGroup
export const InputTypeRadioLabel = styled('label')`
  display: inline-flex;
  align-items: center;
  & + & {
    margin-left: 20px;
  }
  &:hover {
    cursor: pointer;
  }
`;

export const InputTypeRadio = styled('input')`
  appearance: radio;
  width: auto;
  height: auto;
  margin: 0 8px 0 0;
`;
InputTypeRadio.propTypes = {
  ...inputPropTypes,
};
InputTypeRadio.defaultProps = {
  ...inputDefaultProps,
  type: 'radio',
  placeholder: '',
};

export const Textarea = styled('textarea')(inputStyles);

Textarea.propTypes = {
  placeholder: PropTypes.string.isRequired,
  id: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool.isRequired,
  rows: PropTypes.number,
};

Textarea.defaultProps = {
  placeholder: '',
  required: false,
  rows: 5,
};

const InputUploadButton = styled(Button)`
  pointer-events: none;
  position: absolute;
  bottom: 0;
  width: 125px;
`;

const InputUploadWrapper = styled('div')`
  display: flex;
`;

const RemoveUploadButton = styled(Button)`
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`;

const UploadButtonWrapper = styled('div')`
  position: relative;
  &:hover {
    button {
      /* because fake button clicks through */
      background-color: ${colors.blue.secondary};
    }
  }
  & + span {
    &:empty {
      display: none;
    }
  }
  label {
    display: block;
    color: ${colors.grey.primary};
    font-size: 13px;
    line-height: 16px;
    font-weight: ${fonts.sans.weight.bold};
    text-transform: uppercase;
    letter-spacing: 0.01em;
    margin: 0;
  }
  input {
    opacity: 0.1;
    height: 30px;
    width: 125px;
    outline: 0;
    &:hover {
      cursor: pointer;
    }
  }
`;

export const InputUpload = (props) => {
  const { Trigger, triggerProps, handleRemoveFile } = props;

  return (
    <InputUploadWrapper>
      <UploadButtonWrapper>
        <Trigger {...triggerProps} />

        <InputUploadButton type="button" size="small" variant="secondary">
          Choose file
        </InputUploadButton>
      </UploadButtonWrapper>
      <RemoveUploadButton size="small" type="button" onClick={handleRemoveFile}>
        <Icon type="x" color={colors.grey.secondaryDark} />
      </RemoveUploadButton>
    </InputUploadWrapper>
  );
};

InputUpload.defaultProps = {
  triggerProps: {},
};

InputUpload.propTypes = {
  Trigger: PropTypes.func.isRequired,
  handleRemoveFile: PropTypes.func.isRequired,
  triggerProps: PropTypes.shape({}),
};
